import { siteConfig } from '@/config/site.config';
import type {
  Event,
  Tournament,
  Activity,
  Addon,
  SpecialEvent,
  FlowConfig,
  StylingConfig,
  Availability,
} from '@/types';

const BASE_URL = siteConfig.portalApiUrl;
const EVENT_ID = siteConfig.eventId;

/**
 * Paginated response format for backward compatibility with components
 * Portal API returns arrays, but some components expect this format
 */
interface PaginatedResponse<T> {
  docs: T[];
  totalDocs: number;
  page: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

/**
 * Wrap an array response in paginated format for component compatibility
 */
function toPaginatedResponse<T>(data: T[]): PaginatedResponse<T> {
  return {
    docs: data,
    totalDocs: data.length,
    page: 1,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
  };
}

class PortalAPI {
  private async fetch<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${BASE_URL}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || `API Error: ${response.statusText}`);
    }

    return response.json();
  }

  // Event data
  async getEvent(): Promise<Event> {
    return this.fetch<Event>(`/events/${EVENT_ID}`);
  }

  // Tournaments
  async getTournaments(params?: {
    status?: string;
    visible?: boolean;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<Tournament>> {
    const tournaments = await this.fetch<Tournament[]>(`/events/${EVENT_ID}/tournaments`);

    // Client-side filtering if params provided
    // Portal already filters to visible=true and status in ['open', 'full', 'closed']
    let filtered = tournaments;
    if (params?.status) {
      filtered = filtered.filter((t) => t.status === params.status);
    }

    return toPaginatedResponse(filtered);
  }

  async getTournament(id: string): Promise<Tournament> {
    const tournaments = await this.fetch<Tournament[]>(`/events/${EVENT_ID}/tournaments`);
    const tournament = tournaments.find((t) => t.id === id);
    if (!tournament) {
      throw new Error('Tournament not found');
    }
    return tournament;
  }

  async getTournamentBySlug(slug: string): Promise<Tournament> {
    const tournaments = await this.fetch<Tournament[]>(`/events/${EVENT_ID}/tournaments`);
    const tournament = tournaments.find((t) => t.slug === slug);
    if (!tournament) {
      throw new Error('Tournament not found');
    }
    return tournament;
  }

  // Activities
  async getActivities(params?: {
    type?: string;
    status?: string;
    visible?: boolean;
  }): Promise<PaginatedResponse<Activity>> {
    const activities = await this.fetch<Activity[]>(`/events/${EVENT_ID}/activities`);

    // Client-side filtering if params provided
    let filtered = activities;
    if (params?.type) {
      filtered = filtered.filter((a) => a.type === params.type);
    }
    if (params?.status) {
      filtered = filtered.filter((a) => a.status === params.status);
    }

    return toPaginatedResponse(filtered);
  }

  // Addons
  async getAddons(params?: {
    category?: string;
    isActive?: boolean;
    featured?: boolean;
  }): Promise<PaginatedResponse<Addon>> {
    const addons = await this.fetch<Addon[]>(`/events/${EVENT_ID}/addons`);

    // Client-side filtering if params provided
    let filtered = addons;
    if (params?.category) {
      filtered = filtered.filter((a) => a.category === params.category);
    }
    if (params?.featured !== undefined) {
      filtered = filtered.filter((a) => a.featured === params.featured);
    }

    return toPaginatedResponse(filtered);
  }

  // Special events
  async getSpecialEvents(params?: {
    status?: string;
    visible?: boolean;
  }): Promise<PaginatedResponse<SpecialEvent>> {
    const specialEvents = await this.fetch<SpecialEvent[]>(`/events/${EVENT_ID}/special-events`);

    // Client-side filtering if params provided
    let filtered = specialEvents;
    if (params?.status) {
      filtered = filtered.filter((se) => se.status === params.status);
    }

    return toPaginatedResponse(filtered);
  }

  // Real-time availability
  async getAvailability(): Promise<Availability> {
    return this.fetch<Availability>(`/events/${EVENT_ID}/availability`);
  }

  // Styling config - extracted from event response
  async getStylingConfig(): Promise<StylingConfig | null> {
    const event = await this.getEvent();
    return event.stylingConfig || null;
  }

  // Flow config - extracted from event response
  async getFlowConfig(): Promise<FlowConfig | null> {
    const event = await this.getEvent();
    return event.flowConfig || null;
  }
}

export const portalAPI = new PortalAPI();
